import React, { Component } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';

import ThemeIcons from '../../../theme/Icons';
import ThemeContext from '../../../theme/ThemeContext';

import Button from '../../misc/Button';
import Popover from '../../misc/Popover';
import ConditionalLogicSettingsForm from './ConditionalLogicSettingsForm';
import CollectionConditionalLogicSettingsForm from './CollectionConditionalLogicSettingsForm';

const StyledWrapper = styled.div`
  display: flex;
  flex-direction: column;
`;

const StyledPopoverBody = styled.div`
  height: 100%;
  font-family: ${(props) => props.theme.fontFamily};
  padding: 16px;
  display: flex;
  flex-direction: column;
  width: 300px;
  ${(props) => props.hasSchema && `
    max-height: 400px;
    overflow-y: auto;
  `}
`;

const StyledActionWrapper = styled.div`
  display: flex;
  flex-direction: row;
`;

const StyledActionCol = styled.div`
  display: flex;
  flex-direction: column;
`;

const StyledActionLabel = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
`;

const StyledActionLabelText = styled.label`
  font-size: 14px;
  margin-bottom: 2px;
  font-weight: 600;
  font-size: 12px;
  color: ${(props) => props.theme.fontColorPrimary};
`;

const StyledActionLabelCount = styled.label`
  font-size: 14px;
  margin-left: 6px;
  margin-bottom: 2px;
  font-weight: 600;
  font-size: 12px;
  color: ${(props) => props.theme.fontColorPrimary};
  flex: 1;
  width: 50%;
`;

const StyledActionDesc = styled.label`
  font-size: 12px;
  color: ${(props) => props.theme.fontColorSecondary};
  flex: 1;
`;

const StyledErrorMesssage = styled.div`
  padding: 6px;
  color: ${(props) => props.theme.dangerColor};
  font-weight: 500;
  border: 1px solid ${(props) => props.theme.dangerColor};
  border-radius: 5px;
  text-align: center;
  margin-bottom: 8px;
`;

class LogicSettings extends Component {

  static contextType = ThemeContext

  static propTypes = {
    fileId: PropTypes.string,
    targetLabel: PropTypes.string,
    fields: PropTypes.array,
    pages: PropTypes.array,
    logic: PropTypes.object,
    onSubmit: PropTypes.func,
    type: PropTypes.string,
  }

  static defaultProps = {
    logic: {
      action: 'show',
      eval: 'and',
      conditions: []
    }
  }

  constructor(props) {

    super(props);
    this.state = {
      displayModal: false,
      form: props.logic
    };

  }

  componentDidUpdate(prevProps, prevState) {

    const { logic } = this.props;
    const { displayModal } = this.state;

    if (prevProps.logic !== logic) {

      this.setState({ form: logic });

    } else if (prevState.displayModal !== displayModal) {

      this.setState({ error: false, form: logic || { action: 'show', eval: 'and', conditions: [] } });

    }

  }

  render() {

    const { logic, targetLabel, hasSchema } = this.props;
    const { displayModal, form, error } = this.state;

    const conditionCountNode = !logic || !logic.conditions || logic.conditions.length < 1 ? null : (
      <StyledActionLabelCount theme={this.context}>{logic.conditions.length}</StyledActionLabelCount>
    );

    return (
      <StyledWrapper>
        <StyledActionWrapper>
          <StyledActionCol style={{ flex: 1 }}>
            <StyledActionLabel>
              <StyledActionLabelText theme={this.context}>Conditions</StyledActionLabelText>
              {conditionCountNode}
            </StyledActionLabel>
            <StyledActionDesc theme={this.context}>
              Logic to show or hide
              {' '}
              {targetLabel}
            </StyledActionDesc>
          </StyledActionCol>
          <StyledActionCol>
            <Popover
              reposition={false}
              positions={['left', 'top', 'bottom']}
              isOpen={displayModal}
              onClose={this.handleHide}
              onSubmit={this.handleSubmit}
              content={(
                <StyledPopoverBody theme={this.context} hasSchema={hasSchema}>
                  {!error ? null : (
                    <StyledErrorMesssage theme={this.context}>
                      Missing required fields. Please ensure all condition fields are properly configured.
                    </StyledErrorMesssage>
                  )}
                  {hasSchema ? (
                    <CollectionConditionalLogicSettingsForm
                      {...this.props}
                      logic={form}
                      onChange={(nextForm) => this.setState({ form: nextForm })}
                    />
                  ) : (
                    <ConditionalLogicSettingsForm
                      {...this.props}
                      logic={form}
                      onChange={(nextForm) => this.setState({ form: nextForm })}
                    />
                  )}
                </StyledPopoverBody>
              )}
            >
              <div>
                <Button
                  icon={ThemeIcons.settings.logic}
                  styles={{ padding: '6px 8px' }}
                  onClick={(e) => {
                    e.currentTarget.blur();
                    this.setState({ displayModal: true });
                  }}
                />
              </div>
            </Popover>
          </StyledActionCol>
        </StyledActionWrapper>

      </StyledWrapper>
    );

  }

  handleSubmit = () => {

    const { form } = this.state;
    const { onSubmit, hasSchema } = this.props;

    let validLogic = true;

    if (!form.action || !form.eval) validLogic = false;

    const invalidCondionIndex = form.conditions.findIndex((condition) => {
      if (hasSchema) {
        const hasTarget = condition.column || condition.field;
        return !hasTarget || !condition.condition;
      } else {
        return !condition.field || !condition.condition;
      }
    });

    if (invalidCondionIndex !== -1) validLogic = false;

    if (validLogic && onSubmit) {

      const cleanedForm = this.getCleanedForm(form);

      onSubmit(cleanedForm);

      this.handleHide();

    } else if (!validLogic) {
      this.setState({ error: true });
    }

  }

  /**
   * This method is responsible for removing the extra space at
   * the beginning and end of the values for text fields
   */

  getCleanedForm = (form) => {

    if (!form) return null;

    const nextForm = { ...form };

    // Normalize conditions to always be an array
    const nextFormConditions = Array.isArray(nextForm.conditions)
      ? [...nextForm.conditions]
      : [];

    const cleanedFormConditions = nextFormConditions.map((condition) => {
      if (
        (condition.field || condition.column) &&
      typeof condition.value === 'string' &&
      condition.value
      ) {
        return {
          ...condition,
          value: condition.value.trim(),
        };
      }

      return condition;
    });

    return {
      ...nextForm,
      conditions: cleanedFormConditions,
    };

  }

  handleHide = () => {

    this.setState({ displayModal: false, error: false });

  }

}

export default LogicSettings;
